const IS_DEV = process.env.NODE_ENV !== 'production';
const DEV_API_PORT = 8000;
const DEV_API_BASE = `http://localhost:${DEV_API_PORT}/api`;

// Хосты, на которых бэкенд отдельно от текущего origin (SPA обращается кросс-доменно).
// Все прочие хосты считаются "self-hosted через nginx" — используется относительный /api,
// который nginx прокидывает в backend-контейнер. Так исчезает хардкод порта и URL-а.
const KNOWN_STANDALONE_HOSTS = {
  'saqshy.qr-fabriqa.kz': 'https://saqshy.qr-fabriqa.kz/api',
};

function detectApiBase() {
  try {
    if (typeof window !== 'undefined' && window?.location?.hostname) {
      const host = window.location.hostname;
      // Локальная dev-разработка: фронт по localhost, бэк отдельно на :8000
      if (host === 'localhost' || host === '127.0.0.1') {
        return IS_DEV ? DEV_API_BASE : `${window.location.origin}/api`;
      }
      // Известные stand-alone деплои
      if (KNOWN_STANDALONE_HOSTS[host]) return KNOWN_STANDALONE_HOSTS[host];
      // Dev-server (npm run serve) на не-localhost — бэк на том же хосте, порт 8000
      if (IS_DEV) return `http://${host}:${DEV_API_PORT}/api`;
      // Production build за nginx: относительный путь, всё через тот же origin
      return `${window.location.origin}/api`;
    }
  } catch {
    // ignore
  }
  return IS_DEV ? DEV_API_BASE : '/api';
}

const API_BASE = detectApiBase();
const ACCESS_KEY = 'access';
const REFRESH_KEY = 'refresh';

let isRefreshing = false;
let queue = [];

let activeDate = null;
let bookContexts = new Map();
let chpoContexts = new Map();
let loadingBookPromise = null;
let loadingChpoPromise = null;

function todayISO() {
  const d = new Date();
  const two = (n) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

export function setActiveDate(dateStr) {
  if (typeof dateStr === 'string') {
    const trimmed = dateStr.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      activeDate = trimmed;
    } else {
      throw new Error(`Некорректный формат даты: ${dateStr}`);
    }
  } else if (dateStr instanceof Date) {
    const d = dateStr; const two = (n) => (n < 10 ? `0${n}` : String(n));
    activeDate = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
  } else {
    activeDate = todayISO();
  }
}

// Expose detected API base for places that need to build absolute URLs
// (e.g. media files returned as relative /media/... paths).
export function getApiBase() {
  return API_BASE;
}

export function getBackendOrigin() {
  try {
    const u = new URL(API_BASE);
    // Remove trailing /api or /api/ from pathname
    u.pathname = u.pathname.replace(/\/?api\/?$/, '/');
    u.search = '';
    u.hash = '';
    return u.origin;
  } catch {
    return '';
  }
}
export function getBookContext(date) { return bookContexts.get(date); }
export function getChpoContext(date) { return chpoContexts.get(date); }
export function setBookContext(date, ctx) { bookContexts.set(date, ctx); }
export function setChpoContext(date, ctx) { chpoContexts.set(date, ctx); }
export function getActiveDate() { return activeDate; }
export function invalidateBookContext(date) { bookContexts.delete(date); }
export function invalidateChpoContext(date) { chpoContexts.delete(date); }

async function fetchCheck(type, dateStr) {
  const endpoint = type === 'book' ? 'book/check/' : 'chpo/check/';
  try {
    const { data } = await http.post(endpoint, { date: dateStr });
    return data;
  } catch (err) {
    err.type = type;
    throw err;
  }
}

let loadingBookDate = null;
let loadingChpoDate = null;

export async function loadContext(type, dateISO) {
  if (type === 'book') {
    if (bookContexts.has(dateISO)) return bookContexts.get(dateISO);
    if (loadingBookPromise && loadingBookDate === dateISO) return loadingBookPromise;

    loadingBookDate = dateISO;
    loadingBookPromise = (async () => {
      try {
        const ctx = await fetchCheck('book', dateISO);
        bookContexts.set(dateISO, ctx);
        return ctx;
      } finally {
        loadingBookPromise = null;
        loadingBookDate = null;
      }
    })();
    return loadingBookPromise;
  }
  if (type === 'chpo') {
    if (chpoContexts.has(dateISO)) return chpoContexts.get(dateISO);
    if (loadingChpoPromise && loadingChpoDate === dateISO) return loadingChpoPromise;

    loadingChpoDate = dateISO;
    loadingChpoPromise = (async () => {
      try {
        const ctx = await fetchCheck('chpo', dateISO);
        chpoContexts.set(dateISO, ctx);
        return ctx;
      } finally {
        loadingChpoPromise = null;
        loadingChpoDate = null;
      }
    })();
    return loadingChpoPromise;
  }
  return null;
}

async function ensureContext(type) {
  if (!activeDate) activeDate = todayISO();
  return loadContext(type, activeDate);
}

export function getAccess() { try { return localStorage.getItem(ACCESS_KEY) || ''; } catch { return ''; } }
export function getRefresh() { try { return localStorage.getItem(REFRESH_KEY) || ''; } catch { return ''; } }
export function setAccess(t) { try { localStorage.setItem(ACCESS_KEY, t || ''); } catch { } }
export function setRefresh(t) { try { localStorage.setItem(REFRESH_KEY, t || ''); } catch { } }

async function doFetch(url, opts) {
  return fetch(url, { cache: 'no-store', credentials: 'omit', ...opts });
}
async function parseJSON(res) {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}
function joinUrl(base, path) {
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const p = path.startsWith('/') ? path.slice(1) : path;
  return `${b}/${p}`;
}
function withQuery(path, params) {
  let url = joinUrl(API_BASE, path);
  if (params && typeof params === 'object') {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) sp.append(k, String(v));
    }
    const qs = sp.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  return url;
}
function buildError(payload, status, path, method) {
  const err = new Error(payload?.detail || payload?.message || `HTTP ${status}`);
  err.status = status; err.payload = payload; err.path = path; err.method = method; return err;
}

const AUTH_WHITELIST = ['users/login/', 'users/token/refresh/'];
const isAuthPath = (p) => typeof p === 'string' && AUTH_WHITELIST.some(seg => p.includes(seg));

async function request(path, { method = 'GET', params, data, headers } = {}, _retry = false) {
  // === Автоконтекст ===
  // NOTE: book/users endpoints use `book_id` (and backend model uses `chpo`),
  // so auto-injecting `book` breaks validation there.
  const isBookPath = path.startsWith('book/') && path !== 'book/check/' && !path.startsWith('book/users');
  // Справочники (duty-types и т.п.) — тоже НЕ должны получать авто-CHPO id,
  // они не привязаны к конкретному дню.
  const isChpoPath = path.startsWith('chpo/')
    && path !== 'chpo/check/'
    && !path.startsWith('chpo/duty-types');
  // Не используем контекст на auth эндпоинтах
  if (!isAuthPath(path)) {
    if (isBookPath) {
      const ctx = await ensureContext('book');
      // Добавляем book id
      if (method === 'GET') {
        params = params || {};
        if (params.book === undefined) params.book = ctx?.id;
      } else if (data && typeof data === 'object') {
        if (data.book === undefined) data.book = ctx?.id;
      } else if (data === undefined && ['POST', 'PUT', 'PATCH'].includes(method)) {
        data = { book: ctx?.id };
      }
    } else if (isChpoPath) {
      const ctx = await ensureContext('chpo');
      if (method === 'GET') {
        params = params || {};
        if (params.chpo === undefined) params.chpo = ctx?.id;
      } else if (data && typeof data === 'object') {
        if (data.chpo === undefined) data.chpo = ctx?.id;
      } else if (data === undefined && ['POST', 'PUT', 'PATCH'].includes(method)) {
        data = { chpo: ctx?.id };
      }
    }
  }

  const url = withQuery(path, params);
  const h = { Accept: 'application/json', ...(headers || {}) };

  // Не добавляем Authorization на auth-эндпоинтах
  const authEndpoint = isAuthPath(path);
  const token = !authEndpoint ? getAccess() : '';
  if (token) h.Authorization = `Bearer ${token}`;

  let body;
  if (data !== undefined) {
    if (!h['Content-Type']) h['Content-Type'] = 'application/json';
    body = h['Content-Type'].includes('application/json') ? JSON.stringify(data) : data;
  }

  const res = await doFetch(url, { method, headers: h, body });

  if (res.ok) {
    const payload = await parseJSON(res);
    // Invalidate context after mutations
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      if (isBookPath) {
        invalidateBookContext(activeDate);
      } else if (isChpoPath) {
        invalidateChpoContext(activeDate);
      }
    }
    return { data: payload, status: res.status, ok: true };
  }

  // На 401 не пытаемся рефрешиться для auth-эндпоинтов
  if (res.status === 401 && !_retry && !authEndpoint) {
    if (isRefreshing) {
      const newToken = await new Promise((resolve, reject) => queue.push({ resolve, reject }));
      const retryHeaders = { ...(headers || {}), Authorization: `Bearer ${newToken}` };
      return request(path, { method, params, data, headers: retryHeaders }, true);
    }

    isRefreshing = true;
    try {
      const refresh = getRefresh();
      if (!refresh) throw new Error('No refresh token');
      const r = await doFetch(withQuery('users/token/refresh/', null), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!r.ok) throw new Error(`Refresh failed: ${r.status}`);
      const refData = await parseJSON(r);
      const newAccess = refData?.access;
      if (!newAccess) throw new Error('No access in refresh');
      setAccess(newAccess);
      queue.forEach(p => p.resolve(newAccess)); queue = []; isRefreshing = false;
      const retryHeaders = { ...(headers || {}), Authorization: `Bearer ${newAccess}` };
      return request(path, { method, params, data, headers: retryHeaders }, true);
    } catch (e) {
      queue.forEach(p => p.reject(e)); queue = []; isRefreshing = false;
      setAccess(''); setRefresh('');
      try { localStorage.removeItem('current_user'); } catch { }
      const payload = await parseJSON(res).catch(() => ({}));
      throw buildError(payload, res.status, path, method);
    }
  }

  const payload = await parseJSON(res).catch(() => ({}));
  throw buildError(payload, res.status, path, method);
}

export const http = {
  get: (p, c = {}) => request(p, { ...c, method: 'GET' }),
  delete: (p, c = {}) => request(p, { ...c, method: 'DELETE' }),
  post: (p, d, c = {}) => request(p, { ...c, method: 'POST', data: d }),
  patch: (p, d, c = {}) => request(p, { ...c, method: 'PATCH', data: d }),
  put: (p, d, c = {}) => request(p, { ...c, method: 'PUT', data: d }),
};
